import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Agents Evolution nodes - comparing traditional agents vs Artemis
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisAgentsEvolutionPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // Traditional Agents Section
    {
      id: 'traditional-label',
      data: { label: '🤖 Traditional Agent Workflows' },
      position: { x: 200, y: 50 },
      style: { ...themeStyles.metricNode, width: 250, background: '#E8E8E8', color: '#424242' },
    },
    
    // Developer request
    {
      id: 'dev-request-traditional',
      data: { label: '👨‍💻 Developer Request\n"Refactor authentication system"' },
      position: { x: 200, y: 120 },
      style: themeStyles.userNode,
    },
    
    // Traditional agents
    {
      id: 'claude-agent',
      data: { label: '🤖 Claude Agent' },
      position: { x: 50, y: 220 },
      style: themeStyles.llmNode,
    },
    {
      id: 'openai-agent',
      data: { label: '🤖 OpenAI Agent' },
      position: { x: 200, y: 220 },
      style: themeStyles.llmNode,
    },
    {
      id: 'custom-agent',
      data: { label: '🤖 Custom Agent' },
      position: { x: 350, y: 220 },
      style: themeStyles.llmNode,
    },
    
    // Agent outputs
    {
      id: 'claude-output',
      type: 'collapsibleCode',
      data: { 
        filename: 'claude_branch/auth.py',
        code: `# Claude's approach - functional
def authenticate(username, password):
    user = db.get_user(username)
    if user and check_password(password, user.hash):
        return create_jwt_token(user.id)
    return None`,
        score: 3.5
      },
      position: { x: 0, y: 320 },
    },
    {
      id: 'openai-output',
      type: 'collapsibleCode',
      data: { 
        filename: 'openai_branch/auth.py',
        code: `# OpenAI's approach - class-based
class AuthService:
    def __init__(self, db, jwt_service):
        self.db = db
        self.jwt = jwt_service
    
    def authenticate(self, credentials):
        # Implementation...`,
        score: 3.7
      },
      position: { x: 250, y: 320 },
    },
    
    // Traditional decision point
    {
      id: 'traditional-decision',
      data: { label: '❓ Developer Decision\nWhich approach is better?\nNo objective metrics' },
      position: { x: 150, y: 450 },
      style: { ...themeStyles.problemNode, width: 200 },
    },
    
    // Artemis Section
    {
      id: 'artemis-label',
      data: { label: '🚀 Artemis Agent Evolution Platform' },
      position: { x: 700, y: 50 },
      style: { ...themeStyles.uniqueFeatureNode, width: 300 },
    },
    
    // Developer request to Artemis
    {
      id: 'dev-request-artemis',
      data: { label: '👨‍💻 Developer Request\n"Refactor authentication system"' },
      position: { x: 750, y: 120 },
      style: themeStyles.userNode,
    },
    
    // Artemis orchestrator
    {
      id: 'artemis-orchestrator',
      data: { label: '🧬 Artemis Orchestrator\nManages Multiple Agents' },
      position: { x: 750, y: 200 },
      style: { ...themeStyles.uniqueFeatureNode, width: 200 },
    },
    
    // Artemis-managed agents
    {
      id: 'artemis-claude',
      data: { label: '🤖 Claude' },
      position: { x: 600, y: 280 },
      style: { ...themeStyles.llmNode, width: 100 },
    },
    {
      id: 'artemis-openai',
      data: { label: '🤖 OpenAI' },
      position: { x: 750, y: 280 },
      style: { ...themeStyles.llmNode, width: 100 },
    },
    {
      id: 'artemis-custom',
      data: { label: '🤖 Custom' },
      position: { x: 900, y: 280 },
      style: { ...themeStyles.llmNode, width: 100 },
    },
    
    // Artemis evaluation
    {
      id: 'artemis-evaluation',
      data: { label: '📊 Automated Evaluation\n• Security: 4.2/5\n• Performance: 3.8/5\n• Maintainability: 4.5/5\n• Test Coverage: 4.0/5' },
      position: { x: 720, y: 360 },
      style: { ...themeStyles.analysisNode, width: 220 },
    },
    
    // Evolution engine
    {
      id: 'evolution-engine',
      type: 'evolution',
      data: { 
        generation: 'Evolution',
        description: 'Combining best aspects',
        scores: {
          'Security': '4.8/5',
          'Performance': '4.6/5',
          'Maintainability': '4.9/5'
        }
      },
      position: { x: 700, y: 460 },
    },
    
    // Evolved solution
    {
      id: 'evolved-solution',
      type: 'collapsibleCode',
      data: { 
        filename: 'evolved_auth.py',
        code: `# Evolved solution - best of all approaches
@dataclass
class AuthConfig:
    jwt_expiry: int = 3600
    max_attempts: int = 3
    
class SecureAuthService:
    """Combines security, performance, and maintainability"""
    
    def __init__(self, db, jwt_service, config: AuthConfig):
        self.db = db
        self.jwt = jwt_service
        self.config = config
        self._cache = LRUCache(maxsize=1000)
    
    @rate_limit(max_attempts=3)
    @timing_metric
    async def authenticate(self, credentials: AuthCredentials):
        # Best practices from all agents combined
        user = await self._get_user_cached(credentials.username)
        if not user:
            return AuthResult(success=False, reason="User not found")
        
        if await self._verify_password_secure(credentials.password, user):
            token = self._create_secure_token(user)
            await self._log_successful_auth(user)
            return AuthResult(success=True, token=token)
        
        await self._log_failed_attempt(credentials.username)
        return AuthResult(success=False, reason="Invalid credentials")`,
        score: 4.8
      },
      position: { x: 650, y: 600 },
    },
    
    // Future vision
    {
      id: 'future-vision-label',
      data: { label: '🔮 Future: Artemis as Agent Ecosystem Manager' },
      position: { x: 750, y: 850 },
      style: { ...themeStyles.uniqueFeatureNode, width: 350 },
    },
    
    // Human evaluation enhancement
    {
      id: 'human-enhancement',
      data: { label: '👥 Human Decision Support\n• Visual comparisons\n• Objective metrics\n• Risk assessments\n• Educational insights' },
      position: { x: 550, y: 950 },
      style: { ...themeStyles.analysisNode, width: 200 },
    },
    
    // Agent marketplace
    {
      id: 'agent-marketplace',
      data: { label: '🏪 Agent Marketplace\n• Specialized agents\n• Domain experts\n• Custom models\n• Community agents' },
      position: { x: 950, y: 950 },
      style: { ...themeStyles.analysisNode, width: 180 },
    },
    
    // Continuous learning
    {
      id: 'continuous-learning',
      data: { label: '🧠 Continuous Learning\n• Learn from outcomes\n• Improve scoring\n• Adapt to patterns\n• User preferences' },
      position: { x: 750, y: 1050 },
      style: { ...themeStyles.evolvedNode, width: 200 },
    },
    
    // Key differentiators
    {
      id: 'differentiator-1',
      data: { label: '✨ Goes Beyond Agents\nEvolutionary optimization' },
      position: { x: 1150, y: 200 },
      style: { ...themeStyles.metricNode, width: 180 },
    },
    {
      id: 'differentiator-2',
      data: { label: '📊 Objective Scoring\nData-driven decisions' },
      position: { x: 1150, y: 300 },
      style: { ...themeStyles.metricNode, width: 180 },
    },
    {
      id: 'differentiator-3',
      data: { label: '🎯 Multi-objective\nBalance trade-offs' },
      position: { x: 1150, y: 400 },
      style: { ...themeStyles.metricNode, width: 180 },
    },
    {
      id: 'differentiator-4',
      data: { label: '📈 Continuous Improvement\nLearns and adapts' },
      position: { x: 1150, y: 500 },
      style: { ...themeStyles.metricNode, width: 180 },
    }
  ];
};

const agentsEvolutionEdges = [
  // Traditional flow
  { id: 't1', source: 'dev-request-traditional', target: 'claude-agent', style: themeStyles.edgeStyle },
  { id: 't2', source: 'dev-request-traditional', target: 'openai-agent', style: themeStyles.edgeStyle },
  { id: 't3', source: 'dev-request-traditional', target: 'custom-agent', style: themeStyles.edgeStyle },
  { id: 't4', source: 'claude-agent', target: 'claude-output', style: themeStyles.edgeStyle },
  { id: 't5', source: 'openai-agent', target: 'openai-output', style: themeStyles.edgeStyle },
  { id: 't6', source: 'claude-output', target: 'traditional-decision', style: themeStyles.edgeStyle },
  { id: 't7', source: 'openai-output', target: 'traditional-decision', style: themeStyles.edgeStyle },
  
  // Artemis flow
  { id: 'a1', source: 'dev-request-artemis', target: 'artemis-orchestrator', animated: true, style: themeStyles.edgeStyle },
  { id: 'a2', source: 'artemis-orchestrator', target: 'artemis-claude', style: themeStyles.edgeStyle },
  { id: 'a3', source: 'artemis-orchestrator', target: 'artemis-openai', style: themeStyles.edgeStyle },
  { id: 'a4', source: 'artemis-orchestrator', target: 'artemis-custom', style: themeStyles.edgeStyle },
  { id: 'a5', source: 'artemis-claude', target: 'artemis-evaluation', style: themeStyles.edgeStyle },
  { id: 'a6', source: 'artemis-openai', target: 'artemis-evaluation', style: themeStyles.edgeStyle },
  { id: 'a7', source: 'artemis-custom', target: 'artemis-evaluation', style: themeStyles.edgeStyle },
  { id: 'a8', source: 'artemis-evaluation', target: 'evolution-engine', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'a9', source: 'evolution-engine', target: 'evolved-solution', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Differentiators
  { id: 'd1', source: 'artemis-orchestrator', target: 'differentiator-1', style: { ...themeStyles.edgeStyle, strokeDasharray: '3 3' } },
  { id: 'd2', source: 'artemis-evaluation', target: 'differentiator-2', style: { ...themeStyles.edgeStyle, strokeDasharray: '3 3' } },
  { id: 'd3', source: 'evolution-engine', target: 'differentiator-3', style: { ...themeStyles.edgeStyle, strokeDasharray: '3 3' } },
  { id: 'd4', source: 'evolved-solution', target: 'differentiator-4', style: { ...themeStyles.edgeStyle, strokeDasharray: '3 3' } },
  
  // Future vision
  { id: 'f1', source: 'evolved-solution', target: 'future-vision-label', style: themeStyles.edgeStyle },
  { id: 'f2', source: 'future-vision-label', target: 'human-enhancement', style: themeStyles.edgeStyle },
  { id: 'f3', source: 'future-vision-label', target: 'agent-marketplace', style: themeStyles.edgeStyle },
  { id: 'f4', source: 'future-vision-label', target: 'continuous-learning', style: themeStyles.edgeStyle },
  { id: 'f5', source: 'human-enhancement', target: 'continuous-learning', style: themeStyles.feedbackEdgeStyle },
  { id: 'f6', source: 'agent-marketplace', target: 'continuous-learning', style: themeStyles.feedbackEdgeStyle },
];

const ArtemisAgentsEvolution = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(agentsEvolutionEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisAgentsEvolutionPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisAgentsEvolutionPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F5F7FA 0%, #C3CFE2 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#1A202C', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          🤖 Artemis vs Traditional Agents - The Evolution Advantage
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(26, 32, 44, 0.08)',
          borderRadius: '20px',
          color: '#2D3748',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          Traditional agents work in isolation - Artemis orchestrates, evaluates, and evolves their outputs
        </div>

        {/* Reset button */}
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#4A5568',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
            zIndex: 100,
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#2D3748'}
          onMouseLeave={(e) => e.target.style.background = '#4A5568'}
        >
          Reset Layout
        </button>
        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
              minZoom: 0.25,
              maxZoom: 1.0
            }}
            minZoom={0.2}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.4 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(255,255,255,0.8)',
                border: '1px solid #C3CFE2'
              }}
              nodeColor={(node) => {
                return node.style?.background || '#4A5568';
              }}
              nodeStrokeColor={() => '#2D3748'}
              nodeBorderRadius={2}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="#C3CFE2" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Key Insights Panel */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          background: 'rgba(255,255,255,0.95)',
          padding: '20px',
          borderRadius: '15px',
          border: '1px solid #C3CFE2',
          color: '#1A202C',
          fontSize: '13px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          maxWidth: '400px'
        }}>
          <div style={{ marginBottom: '15px', fontWeight: '700', fontSize: '16px', color: '#F4511E' }}>
            🚀 The Future of AI Development
          </div>
          <div style={{ marginBottom: '15px' }}>
            <div style={{ fontWeight: '600', marginBottom: '5px', color: '#5E60CE' }}>
              Current State: Agent Overload
            </div>
            <div style={{ fontSize: '12px', lineHeight: '1.5', color: '#4A5568' }}>
              Developers juggle multiple AI agents, each with different outputs, 
              no standardized evaluation, and manual decision-making.
            </div>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <div style={{ fontWeight: '600', marginBottom: '5px', color: '#5E60CE' }}>
              Artemis Solution: Intelligent Orchestration
            </div>
            <div style={{ fontSize: '12px', lineHeight: '1.5', color: '#4A5568' }}>
              • <strong>Manages</strong> multiple agents simultaneously<br/>
              • <strong>Evaluates</strong> outputs with objective metrics<br/>
              • <strong>Evolves</strong> solutions using best aspects from each<br/>
              • <strong>Empowers</strong> humans with data-driven insights
            </div>
          </div>
          <div style={{ 
            padding: '10px', 
            background: 'rgba(244, 81, 30, 0.1)', 
            borderRadius: '10px',
            borderLeft: '4px solid #F4511E'
          }}>
            <strong>Key Insight:</strong> In the AI age, humans need to be excellent 
            evaluators. Artemis provides the tools, metrics, and insights to make 
            informed decisions about AI-generated code.
          </div>
        </div>

        {/* Comparison Table */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          background: 'rgba(255,255,255,0.95)',
          padding: '15px',
          borderRadius: '15px',
          border: '1px solid #C3CFE2',
          fontSize: '12px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
        }}>
          <table style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #C3CFE2' }}>
                <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600' }}>Feature</th>
                <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600' }}>Traditional</th>
                <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#F4511E' }}>Artemis</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                <td style={{ padding: '8px' }}>Agent Management</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>Manual</td>
                <td style={{ padding: '8px', textAlign: 'center', color: '#48BB78' }}>✓ Automated</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                <td style={{ padding: '8px' }}>Output Evaluation</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>Subjective</td>
                <td style={{ padding: '8px', textAlign: 'center', color: '#48BB78' }}>✓ Objective Metrics</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                <td style={{ padding: '8px' }}>Solution Quality</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>Agent-limited</td>
                <td style={{ padding: '8px', textAlign: 'center', color: '#48BB78' }}>✓ Evolved & Optimized</td>
              </tr>
              <tr>
                <td style={{ padding: '8px' }}>Learning</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>None</td>
                <td style={{ padding: '8px', textAlign: 'center', color: '#48BB78' }}>✓ Continuous</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisAgentsEvolution; 